import api from './client'

export interface WaSession {
  id: string; name: string; phoneNumber: string | null
  status: 'created' | 'qr_pending' | 'connected' | 'disconnected' | 'failed'
  isBusiness: boolean; isActive: boolean; profilePicUrl: string | null; createdAt: string
}
export interface WaChat {
  id: string; sessionId: string; isGroup: boolean
  leadId: string | null; lead: { id: string; firstName: string; lastName: string } | null
  displayName: string | null; number: string | null
  labels: string[]; unreadCount: number
  lastMessageAt: string | null; lastMessagePreview: string | null
  isArchived: boolean; profilePicUrl: string | null; isPinned: boolean; isFavorite: boolean; manualUnread: boolean
}
export interface WaGroupMember { number: string | null; name: string | null; isAdmin: boolean }
export interface WaMessage {
  id: string; chatId: string; direction: 'in' | 'out'
  senderDisplay: string | null; type: string; text: string | null
  mediaPath: string | null; mediaMimeType: string | null; mediaFileName: string | null
  quotedWaMessageId: string | null
  status: 'pending' | 'sent' | 'delivered' | 'read' | 'failed'
  errorMessage: string | null; waTimestamp: string
  /** Set once the text has been edited — drives the "Edited" marker. */
  editedAt: string | null; editCount: number
}

/** One stored version of an edited message. `replacedAt` is null for the current one. */
export interface WaMessageVersion {
  version: number
  text: string | null
  replacedAt: string | null
  replacedBy: { id: string; firstName: string; lastName: string } | null
  source: 'crm' | 'whatsapp'
}
export interface WaMessageHistory {
  messageId: string
  editCount: number
  versions: WaMessageVersion[]
}
export interface WaLeadMedia {
  messageId: string; chatId: string
  fileName: string | null; mimeType: string | null; size: number | null
  direction: 'in' | 'out'; sender: string | null; waTimestamp: string
}
export interface WaLabel { id: string; waLabelId: string; name: string; color: string | null }
export interface WaChatNote { id: string; userId: string; authorName: string; content: string; createdAt: string }
export interface WaKnownContact { id: string; firstName: string; lastName: string; description: string | null; phoneNumber: string | null; email: string | null }
export interface KnownContactInput { firstName: string; lastName: string; description?: string; phoneNumber?: string; email?: string }
export interface Paginated<T> { data: T[]; meta: { page: number; limit: number; total: number; totalPages: number } }

const base = (cid: string) => `/companies/${cid}/whatsapp`

export const waApi = {
  // sessions
  listSessions: (cid: string) => api.get<WaSession[]>(`${base(cid)}/sessions`).then(r => r.data),
  createSession: (cid: string, name: string) => api.post(`${base(cid)}/sessions`, { name }).then(r => r.data),
  sessionQr: (cid: string, id: string) => api.get<{ status: string; qr: string | null }>(`${base(cid)}/sessions/${id}/qr`).then(r => r.data),
  restartSession: (cid: string, id: string) => api.post(`${base(cid)}/sessions/${id}/restart`),
  logoutSession: (cid: string, id: string) => api.post(`${base(cid)}/sessions/${id}/logout`),
  updateSession: (cid: string, id: string, body: { name?: string; isActive?: boolean }) => api.patch(`${base(cid)}/sessions/${id}`, body).then(r => r.data),
  deleteSession: (cid: string, id: string) => api.delete(`${base(cid)}/sessions/${id}`),
  // chats
  listChats: (cid: string, params: { sessionId?: string; search?: string; label?: string; archived?: boolean; unlinked?: boolean; favorites?: boolean; unread?: boolean; page?: number; limit?: number }) =>
    api.get<Paginated<WaChat>>(`${base(cid)}/chats`, { params }).then(r => r.data),
  chatDetail: (cid: string, id: string) => api.get<WaChat>(`${base(cid)}/chats/${id}`).then(r => r.data),
  startChat: (cid: string, body: { sessionId: string; leadId?: string; phone?: string }) => api.post<WaChat>(`${base(cid)}/chats/start`, body).then(r => r.data),
  linkLead: (cid: string, id: string, leadId: string | null) => api.patch(`${base(cid)}/chats/${id}/link-lead`, { leadId }).then(r => r.data),
  // WhatsApp only applies an edit within 15 minutes of the original send.
  editMessage: (cid: string, chatId: string, waMessageId: string, text: string) =>
    api.patch<WaMessage>(`${base(cid)}/chats/${chatId}/messages/${waMessageId}`, { text }).then(r => r.data),
  // Administrators and company owners only; other roles get a 403.
  messageEdits: (cid: string, chatId: string, waMessageId: string) =>
    api.get<WaMessageHistory>(`${base(cid)}/chats/${chatId}/messages/${waMessageId}/edits`).then(r => r.data),
  archiveChat: (cid: string, id: string, archived: boolean) => api.patch(`${base(cid)}/chats/${id}/archive`, { archived }),
  pinChat: (cid: string, id: string, pinned: boolean) => api.patch(`${base(cid)}/chats/${id}/pin`, { pinned }),
  favoriteChat: (cid: string, id: string, favorite: boolean) => api.patch(`${base(cid)}/chats/${id}/favorite`, { favorite }),
  markUnread: (cid: string, id: string) => api.post(`${base(cid)}/chats/${id}/mark-unread`),
  groupMembers: (cid: string, id: string) => api.get<WaGroupMember[]>(`${base(cid)}/chats/${id}/group-members`).then(r => r.data),
  renameGroup: (cid: string, id: string, subject: string) => api.patch<WaChat>(`${base(cid)}/chats/${id}/group-subject`, { subject }).then(r => r.data),
  markRead: (cid: string, id: string) => api.post(`${base(cid)}/chats/${id}/read`),
  unreadCount: (cid: string) => api.get<{ total: number }>(`${base(cid)}/unread-count`).then(r => r.data),
  // messages
  history: (cid: string, chatId: string, before?: string, limit?: number) =>
    api.get<{ data: WaMessage[]; meta: { hasMore: boolean } }>(`${base(cid)}/chats/${chatId}/messages`, { params: { before, limit } }).then(r => r.data),
  searchMessages: (cid: string, chatId: string, q: string) =>
    api.get<{ data: WaMessage[] }>(`${base(cid)}/chats/${chatId}/messages/search`, { params: { q } }).then(r => r.data),
  messageContext: (cid: string, chatId: string, aroundId: string) =>
    api.get<{ data: WaMessage[]; meta: { targetId: string; hasMore: boolean; hasMoreAfter: boolean } }>(`${base(cid)}/chats/${chatId}/messages/context`, { params: { aroundId } }).then(r => r.data),
  // lead-scoped (Lead detail page)
  leadChats: (cid: string, leadId: string) =>
    api.get<WaChat[]>(`${base(cid)}/leads/${leadId}/chats`).then(r => r.data),
  leadMedia: (cid: string, leadId: string) =>
    api.get<WaLeadMedia[]>(`${base(cid)}/leads/${leadId}/media`).then(r => r.data),
  sendText: (cid: string, chatId: string, text: string, quotedMessageId?: string) => api.post<WaMessage>(`${base(cid)}/chats/${chatId}/messages`, { text, quotedMessageId }).then(r => r.data),
  sendMedia: (cid: string, chatId: string, file: File, caption?: string) => {
    const fd = new FormData(); fd.append('file', file); if (caption) fd.append('text', caption)
    return api.post<WaMessage>(`${base(cid)}/chats/${chatId}/messages`, fd, { headers: { 'Content-Type': 'multipart/form-data' } }).then(r => r.data)
  },
  retryMessage: (cid: string, id: string) => api.post(`${base(cid)}/messages/${id}/retry`).then(r => r.data),
  retryFailed: (cid: string, chatId: string) => api.post<{ retried: number }>(`${base(cid)}/chats/${chatId}/retry-failed`).then(r => r.data),
  // mediaBlob replaces a raw mediaUrl path — <img src> / <a href> cannot send the Authorization header,
  // so media must be fetched via axios (which injects the token) and served as an object URL.
  mediaBlob: (cid: string, messageId: string) =>
    api.get(`${base(cid)}/messages/${messageId}/media`, { responseType: 'blob' }).then(r => URL.createObjectURL(r.data)),
  // misc
  checkNumber: (cid: string, sessionId: string, phone: string) => api.get<{ exists: boolean }>(`${base(cid)}/check-number`, { params: { sessionId, phone } }).then(r => r.data),
  listLabels: (cid: string, sessionId: string) => api.get<WaLabel[]>(`${base(cid)}/labels`, { params: { sessionId } }).then(r => r.data),
  handleChatLabel: (cid: string, chatId: string, labelId: string, action: 'add' | 'remove') => api.post(`${base(cid)}/chats/${chatId}/labels`, { labelId, action }),
  listNotes: (cid: string, chatId: string) => api.get<WaChatNote[]>(`${base(cid)}/chats/${chatId}/notes`).then(r => r.data),
  createNote: (cid: string, chatId: string, content: string) => api.post(`${base(cid)}/chats/${chatId}/notes`, { content }).then(r => r.data),
  deleteNote: (cid: string, noteId: string) => api.delete(`${base(cid)}/notes/${noteId}`),
  listKnown: (cid: string) => api.get<WaKnownContact[]>(`${base(cid)}/known-contacts`).then(r => r.data),
  createKnown: (cid: string, body: KnownContactInput) => api.post(`${base(cid)}/known-contacts`, body).then(r => r.data),
  updateKnown: (cid: string, id: string, body: KnownContactInput) => api.patch(`${base(cid)}/known-contacts/${id}`, body).then(r => r.data),
  deleteKnown: (cid: string, id: string) => api.delete(`${base(cid)}/known-contacts/${id}`),
  createGroup: (cid: string, body: { sessionId: string; subject: string; leadId: string; knownContactIds: string[] }) => api.post<WaChat>(`${base(cid)}/groups`, body).then(r => r.data),
  addParticipant: (cid: string, chatId: string, knownContactId: string) => api.post(`${base(cid)}/groups/${chatId}/participants`, { knownContactId }),
}
